import { useEffect, useId, useState } from "react";
import { api } from "../api/apiClient";
import { usePhotoCapture } from "./usePhotoCapture";
import { useT } from "../i18n/LanguageProvider";

/**
 * Reusable photo input: lets the user either pick a file or take a photo with
 * the device camera, optionally auto-removes the background via the server-side
 * rembg sidecar, and surfaces a before/after preview with a "use original?"
 * fallback if removal fails.
 *
 * G-116: the flow itself (resize, removal, the review dialog, the hidden
 * inputs) lives in `usePhotoCapture`; this component is the buttons, the
 * auto-remove checkbox and the committed preview around it.
 */

type Props = {
  /** Receives the chosen (and possibly processed) File. Null clears the value. */
  onChange: (file: File | null) => void;
  /** Current value for the parent component (used to drive the preview). */
  value: File | null;
  /** Show "Remove photo" button when a value is set. Default: true. */
  showRemoveButton?: boolean;
  /** Field label shown in the modal title for context. Default: the word for "Photo". */
  label?: string;
  /**
   * G-87: how this surface reaches the background remover. Defaults to the
   * staff route; the kiosk passes its floor-plane twin, because a floor token
   * is rejected on every CRM route and would 401 here.
   *
   * Injected rather than branched on inside, so there stays ONE photo flow
   * with one modal, one fallback and one resize — a second copy for the
   * tablet is how the three `logWaste` callers drifted in G-73.
   */
  removeBackground?: (file: File) => Promise<File>;
};

export function PhotoInput({
  onChange,
  value,
  showRemoveButton = true,
  label,
  removeBackground = api.removeBackground,
}: Props) {
  const { t } = useT();
  const checkboxId = useId();

  const [autoRemoveBackground, setAutoRemoveBackground] = useState(true);

  const capture = usePhotoCapture({
    onCommit: onChange,
    removeBackground,
    title: label ?? t("photoInput.photo"),
    autoRemoveBackground,
  });

  // Live preview of the currently committed value (top of the form).
  const [committedPreviewUrl, setCommittedPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!value) {
      setCommittedPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setCommittedPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  function clearValue() {
    onChange(null);
  }

  return (
    <>
      <div className="photo-input">
        <div className="photo-input-buttons">
          <button
            type="button"
            className="button tinted"
            onClick={capture.openLibrary}
          >
            {t("common.chooseFile")}
          </button>
          <button
            type="button"
            className="button tinted"
            onClick={capture.openCamera}
          >
            {t("common.takePhoto")}
          </button>
        </div>

        <label
          htmlFor={checkboxId}
          className="photo-input-checkbox"
          style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}
        >
          <input
            id={checkboxId}
            type="checkbox"
            checked={autoRemoveBackground}
            onChange={(e) => setAutoRemoveBackground(e.target.checked)}
          />
          <span>{t("photoInput.autoRemoveBackground")}</span>
        </label>

        <p className="field-hint">
          {t("photoInput.fileHint")}{" "}
          {autoRemoveBackground ? t("photoInput.autoRemoveHint") : null}
        </p>

        {committedPreviewUrl ? (
          <div className="item-image-preview">
            <img src={committedPreviewUrl} alt="" />
          </div>
        ) : null}

        {value && showRemoveButton ? (
          <button
            type="button"
            className="button plain"
            onClick={clearValue}
            style={{ alignSelf: "flex-start" }}
          >
            {t("photoInput.removePhoto")}
          </button>
        ) : null}

        {capture.ui}
      </div>
    </>
  );
}
